import { ArrayDiff } from '../core/reconciler/arrayDiff';
import { PropsDiff } from '../core/reconciler/propsDiff';
import { type ModelLogoRenderable, create as createLogo } from '../renderables/modelLogoRenderable';
import { dispose as disposeLogo } from '../renderables/pieceLogoRenderable';
import {
  type ModelRenderable,
  attachModel,
  detachModel,
  recreateGroups,
  recreateMaterials,
} from '../renderables/modelRenderable';
import { getResource, getResourceId } from '../scene/scene';
import type { TModelComponent, TModelLogo } from '../scene/sceneTypes';
import { update as updateLogo } from './modelLogoReconciler';
import type { SceneReconcilerFrame } from './sceneReconcilerFrame';

/**
 *
 * @param renderable
 * @param nextProps
 * @param prevProps
 * @param frame
 */
export const update = (
  renderable: ModelRenderable,
  nextProps: TModelComponent,
  prevProps: TModelComponent | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  let dirty = false;

  const diffProps = PropsDiff.diff(nextProps, prevProps);

  // Model affects geometries and materials
  if (diffProps.includes('model')) {
    dirty = updateModel(renderable, nextProps, prevProps, frame) || dirty;
  }

  // Logos affect materials
  if (diffProps.includes('logos')) {
    dirty = updateLogos(renderable, nextProps.logos, prevProps?.logos, frame) || dirty;
  }

  return dirty;
};

const updateLogos = (
  renderable: ModelRenderable,
  nextProps: TModelLogo[],
  prevProps: TModelLogo[] | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const { resources } = frame;

  const [nextDiff, prevDiff] = ArrayDiff.diff(nextProps ?? [], prevProps ?? []);

  const ls = renderable.logos;
  const map = ls.reduce<Map<string, ModelLogoRenderable>>((map, l) => {
    map.set(l.id, l);
    return map;
  }, new Map());

  ls.length = 0;

  for (const pair of nextDiff) {
    const l = map.get(pair.next._id) ?? createLogo(pair.next._id, pair.next.meshId);
    updateLogo(l, pair.next, pair.prev, frame);
    ls.push(l);
  }

  for (const prev of prevDiff) {
    const l = map.get(prev._id);
    disposeLogo(l, resources);
  }

  recreateMaterials(renderable);
  recreateGroups(renderable);

  return true;
};

const updateModel = (
  renderable: ModelRenderable,
  nextProps: TModelComponent,
  prevProps: TModelComponent | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const { resources } = frame;

  const nextModelId = getResourceId(nextProps.model);
  const prevModelId = prevProps ? getResourceId(prevProps.model) : undefined;

  if (nextModelId === prevModelId) {
    return false;
  }

  if (prevProps) {
    detachModel(renderable, resources);
  }

  let dirty = false;

  if (resources.findModel(nextModelId)) {
    attachModel(renderable, nextModelId, resources);
    dirty = true;
  } else {
    if (!resources.hasModelRequest(nextModelId)) {
      const { nextScene } = frame;
      const nextModel = getResource(nextProps.model, (id) =>
        nextScene.models.find((m) => m.id === id),
      );
      resources.loadModelDeferred(nextModel);
    }
  }

  return dirty;
};
